import { Link } from 'react-router-dom';
import { type GameMode, modeLabel } from '@shared/chart';
import { modePath } from '../game/modeRouting';
import { TrademarkDisclaimer } from './TrademarkDisclaimer';
import './modeSeoSection.css';

/** A single search-intent feature bullet: a bold lead phrase + supporting detail. */
interface LandingFeature {
  label: string;
  detail: string;
}

interface ModeLandingCopy {
  /** Keyword-rich `<h2>` for the crawlable copy block. */
  heading: string;
  /** One or two short paragraphs that restate the target query in prose. */
  paragraphs: string[];
  /** Feature bullets reinforcing "free / no download / unblocked / NCS / custom". */
  features: LandingFeature[];
  /** Keys rendered as `<kbd>` in the how-to line (the mode's default binding). */
  keys: string[];
  /** Sentence shown after the keys describing the core interaction. */
  controls: string;
  /**
   * Whether this mode ships a community browse page. Mirrors
   * `MODE_CAPABILITIES[mode].browse` in `seoCatalogue.ts`; kept local so the
   * client landing copy doesn't pull the server catalogue (and its builtin-map
   * data) into the browser bundle. Only pulse / circles / guitar have browse.
   */
  hasBrowse: boolean;
}

/**
 * Tier-2 "clone gap" landing copy per mode (see `.cursor/rules/seo-overview.mdc`).
 * Each entry targets the winnable long-tail query for its mode — `guitar hero` /
 * `clone hero`, `osu`, `piano tiles`, the `geometry dash wave game`, and the
 * genre `one-button rhythm game` — framed for fair use: competitor marks appear
 * as `-inspired` (or in a comparative "alternative to" / "much like" phrase),
 * never as a bare game we claim to offer, and the {@link TrademarkDisclaimer}
 * below the copy states we are independent and unendorsed.
 */
const MODE_LANDING_COPY: Record<GameMode, ModeLandingCopy> = {
  pulse: {
    heading: 'A one-button rhythm game, free and online',
    paragraphs: [
      'BeatPulse is a free one-button rhythm game you play right in the browser — ' +
        'flip gravity to the beat and land on the pad. One input, easy to learn, ' +
        'hard to master, with nothing to download or install.',
      'Tracks come from the royalty-free NCS catalogue across three densities, and ' +
        'you can play community maps or build your own. It runs on school and work ' +
        'networks, so it doubles as an unblocked rhythm game you can pick up anywhere.',
    ],
    features: [
      { label: 'One button', detail: 'the entire game is timing a single tap' },
      { label: 'Free & online', detail: 'instant play, no download, no account needed' },
      { label: 'Three densities', detail: 'Normal, Hard and Extreme charts per track' },
      { label: 'NCS soundtrack', detail: 'copyright-safe music, plus community maps' },
      { label: 'Unblocked', detail: 'plays in any modern browser, even at school' },
    ],
    keys: ['Space'],
    controls: 'or click / tap — flip gravity onto the pad in time with the beat.',
    hasBrowse: true,
  },
  circles: {
    heading: 'An osu-inspired circle-clicking game, free and unblocked',
    paragraphs: [
      'BeatCircles is a free, browser-based osu-inspired rhythm game — move the cursor, ' +
        'click each circle as its approach ring closes in, and trace the sliders. ' +
        'There is no download and no account needed to start playing online.',
      'Charts run across four difficulty tiers, from a click-only on-ramp to dense ' +
        'streams, all set to NCS tracks. Because it runs entirely in the browser it ' +
        'works as an unblocked osu alternative at school when you just want to play.',
    ],
    features: [
      { label: 'Play online', detail: 'loads instantly, nothing to install' },
      { label: 'osu-inspired', detail: 'circles, sliders and stream patterns' },
      { label: 'Four difficulties', detail: 'from beginner clicks to insane density' },
      { label: 'NCS soundtrack', detail: 'royalty-free music plus community maps' },
      { label: 'Unblocked', detail: 'browser-only, so it works on locked-down networks' },
    ],
    keys: ['Z', 'X'],
    controls: 'or click — move to aim and tap as the approach ring meets the circle.',
    hasBrowse: true,
  },
  piano: {
    heading: 'A Piano Tiles-inspired tile game, free and unblocked',
    paragraphs: [
      'BeatPiano is a free Piano Tiles-inspired game you play in the browser — tap the ' +
        'falling tiles in time with the music across four lanes. No download, no ' +
        'install, instant play.',
      'Charts are built from NCS tracks in three difficulties, so it scales from a ' +
        'steady warm-up to maximum-density tile streams. It loads on school networks ' +
        'too, making it an easy unblocked Piano Tiles alternative.',
    ],
    features: [
      { label: 'No download', detail: 'instant play in any modern browser' },
      { label: 'Piano Tiles-inspired', detail: 'four lanes of falling tiles to tap' },
      { label: 'Three difficulties', detail: 'Normal, Hard and Extreme per track' },
      { label: 'NCS soundtrack', detail: 'copyright-safe music throughout' },
      { label: 'Unblocked', detail: 'runs anywhere there is a browser, school included' },
    ],
    keys: ['Q', 'W', 'E', 'R'],
    controls: 'or tap the lanes on touch — hit the key for each lane as the tile lands.',
    hasBrowse: false,
  },
  guitar: {
    heading: 'A Guitar Hero & Clone Hero-inspired game, free in your browser',
    paragraphs: [
      'BeatGuitar is a free online Guitar Hero-inspired rhythm game you play right in ' +
        'the browser — notes fall down four lanes and you hit the matching fret key ' +
        'as each gem crosses the strike line, much like Clone Hero. No download and ' +
        'no Flash required.',
      'Every track is charted to royalty-free NCS music, and you can play community ' +
        'charts or build your own. It runs on school and work networks, so it is a ' +
        'solid unblocked Guitar Hero / Clone Hero alternative when the real thing is ' +
        'locked down.',
    ],
    features: [
      { label: 'No download', detail: 'plays instantly in any modern browser' },
      { label: 'Four-lane fretboard', detail: 'Clone Hero-inspired charts, three tiers each' },
      { label: 'NCS soundtrack', detail: 'copyright-safe music on every track' },
      { label: 'Custom charts', detail: 'play community maps or build your own' },
      { label: 'Unblocked', detail: 'no plugins, so it works at school and work' },
    ],
    keys: ['D', 'F', 'J', 'K'],
    controls:
      'or tap the lanes on touch — strike the fret as each gem hits the line. Rebind in settings.',
    hasBrowse: true,
  },
  waves: {
    heading: 'A Geometry-Dash-inspired wave game, online and unblocked',
    paragraphs: [
      'BeatWaves is a free one-button wave game inspired by the Geometry Dash ' +
        'wave — hold to climb, release to dive, and thread the corridor at the speed ' +
        'of the song. It plays in the browser with nothing to download.',
      'The corridor is charted to NCS tracks across three difficulties, every gap ' +
        'landing on a real musical onset. It loads on locked-down networks, so it ' +
        'works as an unblocked, Geometry-Dash-inspired wave game wherever you are.',
    ],
    features: [
      { label: 'One button', detail: 'hold to climb, release to dive' },
      { label: 'Geometry-Dash-inspired wave', detail: 'corridor flight synced to the music' },
      { label: 'Three difficulties', detail: 'from a wide corridor to razor-thin tunnels' },
      { label: 'NCS soundtrack', detail: 'every gate lands on a real onset' },
      { label: 'Unblocked', detail: 'browser play with nothing to install' },
    ],
    keys: ['Space'],
    controls:
      'or hold click / touch — hold to fly up, release to fall, and thread the gaps.',
    hasBrowse: false,
  },
  jump: {
    heading: 'A Geometry-Dash-inspired game, online and unblocked',
    paragraphs: [
      'BeatJump is a free one-button platform-rhythm game inspired by Geometry Dash — ' +
        'tap to jump, clear the spikes and land on the blocks in time with the beat. ' +
        'It plays right in the browser with no download.',
      'Every obstacle lands on a real musical onset from the NCS soundtrack, across ' +
        'three difficulty tiers. It runs on school and work networks, so it is a ' +
        'handy unblocked, Geometry-Dash-inspired game when you want a quick run.',
    ],
    features: [
      { label: 'One button', detail: 'tap, click or Space to jump' },
      { label: 'Geometry-Dash-inspired', detail: 'cube runs synced to the music' },
      { label: 'Three difficulties', detail: 'from roomy hops to dense patterns and wider spike walls' },
      { label: 'NCS soundtrack', detail: 'spikes and blocks on real onsets' },
      { label: 'Unblocked', detail: 'browser-only, no install needed' },
    ],
    keys: ['Space'],
    controls:
      'or click / tap — jump to clear spikes and land on blocks, timed to the beat.',
    hasBrowse: false,
  },
};

interface ModeSeoSectionProps {
  mode: GameMode;
  /** App path to the mode's featured/easiest track, for the primary CTA. */
  playHref: string;
  /** Visible label for the play CTA (e.g. "Play On & On"). */
  playLabel: string;
}

/**
 * Crawlable, search-intent copy block rendered below each mode's track list.
 * Aligns the visible body with the keyword-led head meta from
 * {@link import('@shared/seo/seoMeta').modeHomeMeta}: a keyword `<h2>`, prose
 * that restates the target query, feature bullets, a how-to line, and
 * keyword-anchored internal links into play / tutorial / browse.
 */
export function ModeSeoSection({ mode, playHref, playLabel }: ModeSeoSectionProps) {
  const copy = MODE_LANDING_COPY[mode];
  const label = modeLabel(mode);
  const headingId = `mode-seo-${mode}`;
  return (
    <section className="mode-seo" aria-labelledby={headingId} data-testid={`mode-seo-${mode}`}>
      <h2 id={headingId} className="mode-seo-heading">
        {copy.heading}
      </h2>
      {copy.paragraphs.map((paragraph, idx) => (
        <p key={idx} className="mode-seo-text">
          {paragraph}
        </p>
      ))}

      <ul className="mode-seo-features">
        {copy.features.map((feature) => (
          <li key={feature.label}>
            <strong>{feature.label}</strong> — {feature.detail}
          </li>
        ))}
      </ul>

      <div className="mode-seo-how">
        <h3 className="mode-seo-subhead">How to play {label}</h3>
        <p className="mode-seo-text">
          <span className="mode-seo-keys">
            {copy.keys.map((key) => (
              <kbd key={key}>{key}</kbd>
            ))}
          </span>{' '}
          {copy.controls}
        </p>
      </div>

      <div className="home-cta mode-seo-cta">
        <Link to={playHref} className="btn btn-primary">
          {playLabel}
        </Link>
        <Link to={modePath(mode, 'tutorial')} className="btn">
          {label} tutorial
        </Link>
        {copy.hasBrowse && (
          <Link to={modePath(mode, 'browse')} className="btn btn-ghost">
            Browse {label} maps
          </Link>
        )}
      </div>

      <TrademarkDisclaimer />
    </section>
  );
}
